import React, { useState, useEffect } from 'react';
import './GestionEleves.css';
import DashboardHeader from '../components/DashboardHeader';
import AdminNavigationBar from '../components/AdminNavigationBar';
import { DeleteIcon, EditIcon, ViewIcon, UsersIcon, CalendarIcon, SchoolIcon } from '../components/Icons';
import api from '../services/api';

function GestionEleves() {
  const [selectedClass, setSelectedClass] = useState('Toutes les classes');
  const [showAddModal, setShowAddModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStudents, setTotalStudents] = useState(0);

  const [formData, setFormData] = useState({
    nomFamille: '',
    prenom: '',
    dateNaissance: '',
    numero: '',
    numeroNPI: '',
    classe: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedClass]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const endpoint = selectedClass !== 'Toutes les classes'
        ? `/admin/etudiants?classe=${encodeURIComponent(selectedClass)}`
        : '/admin/etudiants';
      
      const studentsResponse = await api.get(endpoint);
      const studentsData = studentsResponse.data || {};
      
      const classesResponse = await api.get('/admin/classes');
      const classesData = classesResponse.data || {};
      
      const studentList = studentsData.data || (Array.isArray(studentsData) ? studentsData : []);
      const classList = Array.isArray(classesData.data) ? classesData.data : (Array.isArray(classesData) ? classesData : []);

      setStudents(studentList);
      setClasses(classList);
      setTotalStudents(studentsData.total || studentList.length);
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données élèves:', error);
      setStudents([]);
      setClasses([]);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const response = await api.post('/admin/etudiants', {
        nom: formData.nomFamille,
        prenom: formData.prenom,
        date_naissance: formData.dateNaissance || null,
        telephone: formData.numero || null,
        classe: formData.classe || null
      });

      if (response.status === 200 || response.status === 201) {
        alert('✅ Élève ajouté avec succès!');
        setShowAddModal(false);
        setFormData({
          nomFamille: '',
          prenom: '',
          dateNaissance: '',
          numero: '',
          numeroNPI: '',
          classe: ''
        });
        fetchData();
      } else {
        alert(`❌ Erreur lors de l'ajout de l'élève`);
        setLoading(false);
      }
    } catch (error) {
      console.error('Erreur:', error);
      const errMsg = error.response?.data?.message || 'Erreur lors de l\'ajout de l\'élève';
      alert(`❌ ${errMsg}`);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élève ?')) return;

    try {
      setLoading(true);
      await api.delete(`/admin/etudiants/${id}`);
      alert('✅ Élève supprimé avec succès!');
      fetchData();
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de la suppression de l\'élève');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <DashboardHeader />
        <AdminNavigationBar 
          title="Gestion des élèves" 
          onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
        />
        <div className="admin-container">
          <main className="admin-content admin-content-full">
            <div className="loading-state">Chargement...</div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des élèves" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="breadcrumb-section" style={{display: 'none'}}>
            <h1>Gestion des élèves</h1>
            <div className="breadcrumb">
              <span>Accueil › Liste des élèves</span>
            </div>
          </div>

          {/* Filter and Stats Section */}
          <div className="filter-stats-section">
            <div className="filter-box">
              <label className="filter-label">Filtre</label>
              <div className="filter-select-wrapper">
                <label className="select-label">Classes</label>
                <select 
                  className="class-filter-select"
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  <option value="Toutes les classes">Toutes les classes</option>
                  {Array.isArray(classes) && classes.map((classe) => {
                    // Safety check to ensure classe is an object with required properties
                    if (typeof classe === 'object' && classe && classe.nom) {
                      return (
                        <option key={classe.id} value={classe.nom}>{classe.nom}</option>
                      );
                    }
                    // Fallback for string values or malformed objects
                    const displayValue = typeof classe === 'string' ? classe : (classe?.nom || 'Classe inconnue');
                    const keyValue = typeof classe === 'object' ? (classe.id || classe.nom) : classe;
                    return (
                      <option key={keyValue} value={displayValue}>{displayValue}</option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="stats-cards-row">
              <div className="stat-card-small">
                <div className="stat-icon-small" style={{ backgroundColor: '#FF6B35' }}>
                  <UsersIcon size={20} />
                </div>
                <div className="stat-info-small">
                  <p className="stat-label-small">Total des Élèves</p>
                  <h3 className="stat-value-small">{totalStudents}</h3>
                  <p className="stat-change-small">Répartis dans 22 classes</p>
                </div>
              </div>

              <div className="teacher-info-card">
                <div className="teacher-avatar-circle">
                  <span style={{fontSize: '32px'}}><SchoolIcon size={32} color="#0078D7" /></span>
                </div>
                <div className="teacher-info-text">
                  <p className="teacher-label">Corps enseignant</p>
                  <h4 className="teacher-name-large">140 Professeurs</h4>
                  <p className="teacher-contact-small">Qualifiés et expérimentés</p>
                </div>
              </div>
            </div>
          </div>

          {/* Students Table */}
          <div className="students-table-card">
            <div className="table-header-section">
              <h3>Liste des élèves {selectedClass !== 'Toutes les classes' && `- ${selectedClass}`}</h3>
              <div className="table-actions">
                <button className="refresh-btn" onClick={fetchData}>🔄</button>
                <button className="add-btn" onClick={() => setShowAddModal(true)}>Ajouter +</button>
              </div>
            </div>

            <div className="table-wrapper">
              <table className="students-table">
                <thead>
                  <tr>
                    <th><input type="checkbox" /></th>
                    <th>Matricule</th>
                    <th>Nom et Prénoms</th>
                    <th>Date de Naissance</th>
                    <th>Classe</th>
                    <th>Téléphone</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td><input type="checkbox" /></td>
                      <td className="matricule-cell">{student.matricule}</td>
                      <td className="student-name">{student.prenom} {student.nom}</td>
                      <td>{formatDate(student.date_naissance || student.dateNaissance)}</td>
                      <td><span className="classe-badge">{student.classe}</span></td>
                      <td>{student.telephone}</td>
                      <td>
                        <span className={`status-badge ${student.actif ? 'active' : 'inactive'}`}>
                          {student.actif ? '✓' : '○'}
                        </span>
                      </td>
                      <td className="action-buttons">
                        <button className="action-btn delete-btn" title="Supprimer">
                          <DeleteIcon size={16} color="#fff" />
                        </button>
                        <button 
                          className="action-btn edit-btn" 
                          title="Modifier" 
                          onClick={() => window.navigateTo && window.navigateTo('vue-etudiant')}
                        >
                          <EditIcon size={16} color="#fff" />
                        </button>
                        <button 
                          className="action-btn view-btn" 
                          title="Voir"
                          onClick={() => window.navigateTo && window.navigateTo('vue-etudiant')}
                        >
                          <ViewIcon size={16} color="#fff" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {students.length === 0 && (
                <div className="empty-state">
                  <p>Aucun élève trouvé pour cette classe</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Add Student Modal */}
      {showAddModal && (
        <div className="add-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="add-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>✕</button>

            <form onSubmit={handleSubmit} className="add-student-form">
              <div className="form-group">
                <label>Nom de famille <span className="required">*</span></label>
                <input
                  type="text"
                  name="nomFamille"
                  placeholder="Entrer le nom"
                  value={formData.nomFamille}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Prénom <span className="required">*</span></label>
                <input
                  type="text"
                  name="prenom"
                  placeholder="Entrer le prénom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Date de Naissance</label>
                <div className="date-input-wrapper">
                  <input
                    type="date"
                    name="dateNaissance"
                    value={formData.dateNaissance}
                    onChange={handleInputChange}
                  />
                  <span className="calendar-icon"><CalendarIcon size={16} /></span>
                </div>
              </div>

              <div className="form-group">
                <label>Téléphone<span className="required">*</span></label>
                <input
                  type="tel"
                  name="numero"
                  placeholder="+225 XX XX XX XX XX"
                  value={formData.numero}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Sélectionnez la classe <span className="required">*</span></label>
                <select
                  name="classe"
                  value={formData.classe}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Veuillez sélectionner</option>
                  {Array.isArray(classes) && classes.map((classe) => {
                    // Safety check to ensure classe is an object with required properties
                    if (typeof classe === 'object' && classe && classe.nom) {
                      return (
                        <option key={classe.id} value={classe.nom}>{classe.nom}</option>
                      );
                    }
                    // Fallback for string values or malformed objects
                    const displayValue = typeof classe === 'string' ? classe : (classe?.nom || 'Classe inconnue');
                    const keyValue = typeof classe === 'object' ? (classe.id || classe.nom) : classe;
                    return (
                      <option key={keyValue} value={displayValue}>{displayValue}</option>
                    );
                  })}
                </select>
              </div>

              <div className="form-group">
                <label>Photo de l'élève</label>
                <div className="upload-box">
                  <div className="upload-icon">📷</div>
                  <p className="upload-text">Télécharger une image</p>
                  <input type="file" className="file-input" accept="image/*" />
                </div>
              </div>

              <button type="submit" className="submit-btn">
                Soumettre →
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionEleves;
